import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useIonRouter } from '@ionic/react'
import { useTimeoutWhen } from 'rooks'

import { useToast } from '../../contexts/toastContext'
import { useBoolean } from '../../hooks/use-boolean'
import { useClientLoyalty } from '../../loyalty/queries'
import {
  setLsDeviceToken,
  useLsDeviceToken,
} from '../../notification/device-token-store'
import { usePushNotificationToken } from '../../notification/push-notification-context'
import { useCheckInMethod } from '../check-in-store'
import {
  useCheckIn,
  useUpdateClientStation,
  useUpdateStationToken,
} from './mutations'
import { useActiveStations } from './queries'

function canUpdateStationToken({
  lsDeviceToken,
  pushToken,
}: {
  lsDeviceToken: string
  pushToken: string | null
}) {
  if (lsDeviceToken && pushToken && lsDeviceToken !== pushToken) return true
  return false
}

function useCheckInApp() {
  const lsDeviceToken = useLsDeviceToken()
  const { pushToken } = usePushNotificationToken()
  const { mutate: updateStationToken, isLoading: isUpdateStationTokenLoading } =
    useUpdateStationToken()

  const activeStations = useActiveStations()
  const isCheckIn = Boolean(
    pushToken && lsDeviceToken && activeStations.data?.length
  )

  useEffect(() => {
    if (pushToken && activeStations.data?.some((d) => d.isClient || d.isPos)) {
      setLsDeviceToken(pushToken)
    }
  }, [activeStations.data, pushToken])

  useEffect(() => {
    if (canUpdateStationToken({ lsDeviceToken, pushToken }) && pushToken) {
      updateStationToken({ deviceTokenNew: pushToken })
    }
  }, [lsDeviceToken, pushToken, updateStationToken])

  useEffect(() => {
    if (isCheckIn && pushToken) {
      setLsDeviceToken(pushToken)
    }
  }, [isCheckIn, pushToken])

  return {
    ...activeStations,
    isCheckInLoading:
      activeStations.isInitialLoading || isUpdateStationTokenLoading,
    isCheckInError: !lsDeviceToken && activeStations.data?.length === 0,
    isCheckIn: Boolean(isCheckIn),
  }
}

function useCheckInLoyaltyRedirect() {
  const [clientStationId, setClientStationId] = useState<string | null>(null)
  const [startTimeout, { on: setStartTimeout }] = useBoolean(false)

  const { data: loyalty } = useClientLoyalty()

  // useParams returns null due to redirect - https://github.com/ionic-team/ionic-framework/issues/28984

  const checkInMethod = useCheckInMethod()
  const { mutate: checkIn, abortCheckIn } = useCheckIn()

  const setToast = useToast()
  const { mutate: updateClientStation } = useUpdateClientStation()
  const router = useIonRouter()

  useTimeoutWhen(
    () => {
      if (!clientStationId)
        return setToast({
          message: 'This station is not properly set up.',
          color: 'danger',
        })

      updateClientStation(
        { objectId: clientStationId, status: 'completed' },
        {
          onSuccess: () => {
            router.push('/check-in/paired/phone-check-in', 'none', 'replace')
          },
        }
      )
    },
    30000, // 30 seconds
    startTimeout
  )

  useEffect(() => {
    async function init() {
      // do not run if loyalty page hasn't finished fetching
      // TODO: possibly refactor this check
      if (!loyalty) return
      checkIn(
        { checkInMethod, clientId: loyalty.client.objectId, type: 'client' },
        {
          onSuccess: (data) => {
            setClientStationId(data.stationClient.objectId)
            setStartTimeout()
          },
        }
      )
    }

    init()
  }, [checkIn, checkInMethod, loyalty, setStartTimeout])

  return {
    clientStationId,
    resetCheckIn: () => {
      abortCheckIn()

      router.push('/check-in/paired/phone-check-in', 'none', 'replace')
    },
  }
}

function useStationClientId() {
  const location = useLocation()

  const searchParams = new URLSearchParams(location.search)
  return searchParams.get('stationClientId') ?? undefined
}

export {
  canUpdateStationToken,
  useCheckInApp,
  useCheckInLoyaltyRedirect,
  useStationClientId,
}
